import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wand2, Globe2, ImageIcon, Sparkles, Film, Search, Megaphone, MapPin,
  Loader2, Check, X, RefreshCw, Lock, AlertTriangle, ArrowRight,
} from "lucide-react";

const KIT_TYPES = [
  { id: "full_brand_kit",  Icon: Sparkles,   title: "Full Brand Kit",        sub: "Logo + website + 30 posts + reels + SEO + ads · everything" },
  { id: "website",         Icon: Globe2,     title: "Website Only",          sub: "Hero copy, sections, FAQs, SEO meta" },
  { id: "logo",            Icon: ImageIcon,  title: "Logo Only",             sub: "Logo concept + palette + Nano Banana render" },
  { id: "social_pack",     Icon: Megaphone,  title: "Social Media Pack",     sub: "FB · IG · LinkedIn · X · 10 ready posts" },
  { id: "google_business", Icon: MapPin,     title: "Google Business Pack",  sub: "5 GMB posts + local SEO copy" },
  { id: "video",           Icon: Film,       title: "Video Pack",            sub: "3 reels + 3 YouTube ideas + promo script" },
  { id: "seo",             Icon: Search,     title: "SEO Pack",              sub: "Keywords, meta, blog topics, schema" },
  { id: "campaign",        Icon: Wand2,      title: "Campaign Pack",         sub: "5 paid-ad campaign suggestions" },
];

export default function MagicTab() {
  const [credits, setCredits] = useState(null);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const [instructions, setInstructions] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const refresh = async () => {
    try {
      const [c, h] = await Promise.all([
        api.get("/magic/credits"),
        api.get("/magic/history"),
      ]);
      setCredits(c.data);
      setHistory(h.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load credits");
    }
  };

  useEffect(() => { refresh(); }, []);

  const generate = async () => {
    if (!selected) return toast.error("Pick a generation type first");
    setGenerating(true);
    setResult(null);
    try {
      const { data } = await api.post("/magic/generate", {
        kit_type: selected,
        instructions: instructions.trim() || null,
        use_saved_profile: true,
      });
      setResult(data);
      toast.success("Generated · saved to Pending Review");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const remaining = credits?.remaining || {};

  return (
    <div className="space-y-8" data-testid="magic-tab-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-fuchsia-400/80 mb-2 inline-flex items-center gap-1.5">
          <Wand2 className="h-3.5 w-3.5" /> Magic Tab — One Click Brand Creation
        </div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">
          Build everything. <span className="bg-clip-text text-transparent bg-gradient-to-r from-fuchsia-300 via-cyan-300 to-violet-300">Instantly.</span>
        </h1>
        <p className="text-white/55 mt-3 max-w-2xl text-sm">
          Pick a kit, click generate, AI produces your full set of marketing assets. Credits are only deducted on a successful generation.
        </p>
      </header>

      {/* Plan & credits strip */}
      {credits && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="magic-credits-strip">
          <CreditTile label="Plan" value={credits.plan_name || "—"} accent="violet" />
          <CreditTile label="Text credits" value={`${remaining.text_credits ?? 0} / ${credits.pool?.text_credits ?? 0}`} accent="cyan" />
          <CreditTile label="Image credits" value={`${remaining.image_credits ?? 0} / ${credits.pool?.image_credits ?? 0}`} accent="emerald" />
          <CreditTile label="Video credits" value={`${remaining.video_credits ?? 0} / ${credits.pool?.video_credits ?? 0}`} accent="rose" />
        </div>
      )}

      {credits && !credits.subscription_active && (
        <div className="glass-strong rounded-2xl p-5 border border-amber-500/40 bg-amber-500/[0.04] flex items-start gap-3" data-testid="magic-no-sub-banner">
          <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong>Your current plan does not allow this action.</strong>
            <span className="text-white/60"> Please renew or upgrade your plan to unlock Magic Tab.</span>
            <a href="/dashboard/billing" className="ml-3 text-cyan-400 underline">View plans →</a>
          </div>
        </div>
      )}

      {/* Kit type selector */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-3">1. Choose what to generate</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="magic-kit-grid">
          {KIT_TYPES.map(({ id, Icon, title, sub }) => (
            <button
              key={id}
              onClick={() => setSelected(id)}
              data-testid={`magic-kit-${id}`}
              className={`text-left p-5 rounded-2xl border-2 transition ${
                selected === id
                  ? "bg-gradient-to-br from-fuchsia-500/15 to-cyan-500/15 border-fuchsia-500/60"
                  : "glass border-transparent hover:border-white/15"
              }`}
            >
              <Icon className={`h-7 w-7 mb-3 ${selected === id ? "text-fuchsia-300" : "text-white/60"}`} />
              <div className="font-heading font-bold text-base">{title}</div>
              <div className="text-xs text-white/45 mt-1 leading-relaxed">{sub}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Instructions + Generate */}
      <div className="glass-strong rounded-2xl p-6 border border-fuchsia-500/20">
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-2">2. Tweak (optional)</div>
        <textarea
          rows={2}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          data-testid="magic-instructions"
          placeholder="Any specific direction? e.g. 'Lean youthful, focus on Bangalore market, emphasise free trial'"
          className="w-full bg-black/40 border border-white/10 focus:border-fuchsia-500/60 outline-none rounded-xl px-4 py-3 text-sm resize-none"
        />
        <button
          onClick={generate}
          disabled={generating || !selected || (credits && !credits.subscription_active)}
          data-testid="magic-generate-btn"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold text-base text-white bg-gradient-to-r from-fuchsia-500 via-violet-600 to-cyan-500 hover:opacity-95 transition disabled:opacity-50"
        >
          {generating ? <Loader2 className="h-5 w-5 animate-spin" /> : <Wand2 className="h-5 w-5" />}
          {generating ? "AI is crafting your kit…" : "Generate Now"}
        </button>
      </div>

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="space-y-3"
            data-testid="magic-result"
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-heading text-xl font-black inline-flex items-center gap-2">
                <Check className="h-5 w-5 text-green-400" /> {KIT_TYPES.find(k => k.id === result.kit_type)?.title}
              </h3>
              <div className="text-xs text-white/50 font-mono">
                {Object.entries(result.credits_consumed || {}).map(([k, v]) => `${v} ${k.replace("_credits","")}`).join(" · ")}
              </div>
            </div>
            {result.logo_image_b64 && (
              <div className="bg-white rounded-xl p-4 max-w-xs">
                <img src={`data:image/png;base64,${result.logo_image_b64}`} alt="logo" className="w-full" />
              </div>
            )}
            <pre className="glass rounded-2xl p-4 text-xs whitespace-pre-wrap break-words text-white/80 max-h-[500px] overflow-auto sidebar-scroll">
              {JSON.stringify(result.result, null, 2)}
            </pre>
          </motion.div>
        )}
      </AnimatePresence>

      {/* History */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">Pending Review · History</div>
          <button onClick={refresh} className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1">
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
        {history.length === 0 ? (
          <div className="glass rounded-2xl p-10 text-center text-sm text-white/50">No generations yet.</div>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="glass rounded-xl p-4 flex items-center justify-between" data-testid={`magic-run-${h.id}`}>
                <div className="min-w-0">
                  <div className="font-bold text-sm">{KIT_TYPES.find(k => k.id === h.kit_type)?.title || h.kit_type}</div>
                  <div className="text-xs text-white/40 font-mono mt-0.5">
                    {new Date(h.created_at).toLocaleString()} · {Object.entries(h.credits_consumed || {}).map(([k,v]) => `${v} ${k.replace("_credits","")}`).join(" · ")}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {h.status === "pending_review" && (
                    <ApprovePublishBtn runId={h.id} onDone={refresh} />
                  )}
                  <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded font-bold ${
                    h.status === "approved" ? "bg-emerald-500/15 text-emerald-300" :
                    h.status === "published" ? "bg-cyan-500/15 text-cyan-300" :
                    h.status === "rejected" ? "bg-rose-500/15 text-rose-300" :
                    "bg-amber-500/15 text-amber-300"
                  }`}>
                    {h.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const ACCENTS = {
  violet: "text-violet-300",
  cyan: "text-cyan-300",
  emerald: "text-emerald-300",
  rose: "text-rose-300",
};

function CreditTile({ label, value, accent }) {
  return (
    <div className="glass rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">{label}</div>
      <div className={`font-heading font-black text-2xl mt-2 ${ACCENTS[accent] || "text-white"}`}>{value}</div>
    </div>
  );
}

function ApprovePublishBtn({ runId, onDone }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/magic/${runId}/approve-and-publish`, { cadence_hours: 24 });
      toast.success(`${data.scheduled_count} posts scheduled · first at ${new Date(data.first_at).toLocaleString()}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={go}
      disabled={busy}
      data-testid={`magic-approve-publish-${runId}`}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-bold bg-gradient-to-r from-cyan-500/25 to-fuchsia-500/25 border border-cyan-400/40 hover:bg-cyan-500/20 disabled:opacity-50"
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
      Approve & Publish
    </button>
  );
}
